import { getUnsyncedScores, markScoresAsSynced } from './scoreManager';
import { supabase } from '../lib/supabase';
import { Score } from '../types';

interface SyncResult {
  success: boolean;
  syncedCount: number;
  errors: string[];
}

export class SyncManager {
  private static instance: SyncManager;
  private syncInProgress = false;
  
  private constructor() {}
  
  static getInstance(): SyncManager {
    if (!SyncManager.instance) {
      SyncManager.instance = new SyncManager();
    }
    return SyncManager.instance;
  }
  
  async syncTournament(tournamentId: string): Promise<SyncResult> {
    if (this.syncInProgress) {
      return { success: false, syncedCount: 0, errors: ['Sync already in progress'] };
    }
    
    if (!navigator.onLine) {
      return { success: false, syncedCount: 0, errors: ['Device is offline'] };
    }
    
    this.syncInProgress = true;
    const result: SyncResult = { success: true, syncedCount: 0, errors: [] };
    
    try {
      const unsyncedScores = getUnsyncedScores(tournamentId);
      
      if (Object.keys(unsyncedScores).length === 0) {
        this.syncInProgress = false;
        return { success: true, syncedCount: 0, errors: [] };
      }
      
      // Process each player's scores
      for (const [playerId, scores] of Object.entries(unsyncedScores)) {
        try {
          // First, get current server state
          const { data: serverData, error: fetchError } = await supabase
            .from('players')
            .select('scores')
            .eq('id', playerId)
            .eq('tournament_id', tournamentId)
            .single();
            
          if (fetchError) {
            result.errors.push(`Failed to fetch server data for player ${playerId}: ${fetchError.message}`);
            continue;
          }
          
          // Merge local and server scores
          const mergedScores = this.mergeScores(serverData?.scores || [], scores);
          
          // Update server with merged scores
          const { error: updateError } = await supabase
            .from('players')
            .update({ 
              scores: mergedScores,
              updated_at: new Date().toISOString()
            })
            .eq('id', playerId)
            .eq('tournament_id', tournamentId);
            
          if (updateError) {
            result.errors.push(`Failed to update server for player ${playerId}: ${updateError.message}`);
            continue;
          }
          
          // Mark scores as synced
          const holeNumbers = scores.map(score => score.hole);
          markScoresAsSynced(tournamentId, playerId, holeNumbers);
          
          result.syncedCount += scores.length;
        } catch (error) {
          result.errors.push(`Error syncing player ${playerId}: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
      
      result.success = result.syncedCount > 0 && result.errors.length === 0;
    } catch (error) {
      result.success = false;
      result.errors.push(`General sync error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      this.syncInProgress = false;
    }
    
    return result;
  }
  
  private mergeScores(serverScores: Score[], localScores: any[]): Score[] {
    // Create a map of hole -> score for server scores
    const serverMap = new Map<number, Score>();
    serverScores.forEach(score => {
      serverMap.set(score.hole, score);
    });
    
    // Merge local scores, keeping the most recent version
    localScores.forEach(localScore => {
      const serverScore = serverMap.get(localScore.hole);
      
      if (!serverScore || localScore.timestamp > (serverScore.timestamp || 0)) {
        // Convert local score to server format
        serverMap.set(localScore.hole, {
          hole: localScore.hole,
          strokes: localScore.strokes,
          netStrokes: localScore.netStrokes || localScore.strokes,
          tournamentId: localScore.tournamentId,
          playerId: localScore.playerId,
          synced: true
        });
      }
    });
    
    // Convert map back to array
    return Array.from(serverMap.values()).sort((a, b) => a.hole - b.hole);
  }
}

// Convenience function
export const syncTournament = async (tournamentId: string): Promise<SyncResult> => {
  return SyncManager.getInstance().syncTournament(tournamentId);
}; 